import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { LargeShareholderFormComponent } from '../large-shareholder-form/large-shareholder-form.component';
import { LargeShareholderService } from '../../../services/large-shareholder.service';
import { LargeShareholderDetailDto } from '../../../models/api/largeShareholderDetailDto';
import { LargeShareholderConnectionDto } from '../../../models/api/largeShareholderConnectionDto';
import { PyramidDtoPerYear } from '../../../models/api/pyramidPerYearDto';
import { PyramidEntitySummary } from '../../../models/pyramidEntitySummary';
import { PyramidEntriesByYearDto } from '../../../models/api/pyramidEntriesByYearDto';
import { LargeShareholderHoldingsResponseDto } from '../../../models/api/largeShareholderHoldingsDto';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { ApiErrorResponse } from '../../../models/api/apiErrorResponse';
import { ErrorCodes } from '../../../models/api/errorCodes';
import { MaterialModule } from '../../../shared/material.module';
import { MatDialog } from '@angular/material/dialog';
import { AddPyramidDialogComponent } from '../add-pyramid-dialog/add-pyramid-dialog.component';
import { EditPyramidDialogComponent } from '../edit-pyramid-dialog/edit-pyramid-dialog.component';
import { AddConnectionDialogComponent } from '../add-connection-dialog/add-connection-dialog.component';
import { DeletePyramidDialogComponent, DeletePyramidDialogResult } from '../delete-pyramid-dialog/delete-pyramid-dialog.component';
import { DeleteEntityYearEvent } from '../pyramidal-item-card/pyramidal-item-card.component';
import { DeletePyramidYearEvent } from '../pyramids-year-card/pyramids-year-card.component';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { CreateConnectionRequest } from '../../../models/api/createConnectionRequest';
import { DeletePyramidYearsRequest } from '../../../models/api/deletePyramidYearsRequest';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-large-shareholder-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LargeShareholderFormComponent,
    NgxSpinnerModule,
    MaterialModule,
    RouterLink
  ],
  templateUrl: './large-shareholder-edit.component.html',
  styleUrls: ['./large-shareholder-edit.component.scss']
})
export class LargeShareholderEditComponent implements OnInit, OnDestroy {
  @ViewChild('largeShareholderForm') largeShareholderForm!: LargeShareholderFormComponent;

  form: FormGroup;
  largeShareholderId: string | null = null;
  largeShareholderData: LargeShareholderDetailDto | null = null;
  connections: LargeShareholderConnectionDto[] = [];
  pyramidEntriesByYear: PyramidDtoPerYear[] = [];
  pyramidEntriesByYearRaw?: PyramidEntriesByYearDto;
  pyramidEntitySummaries: PyramidEntitySummary[] = [];
  holdings: LargeShareholderHoldingsResponseDto | null = null;
  fromCompanyId: string | null = null;
  fromCompanyName: string | null = null;
  fromCompanyYear: string | null = null;
  showYearWarning: boolean = false;
  initialTabIndex = 0;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private largeShareholderService: LargeShareholderService,
    private router: Router,
    private route: ActivatedRoute,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService,
    private dialog: MatDialog,
    private confirmationDialogService: ConfirmationDialogService
  ) {
    this.form = this.fb.group({
      year: ['', Validators.required],
      fullName: ['', Validators.required],
      address: [''],
      website: [''],
      email: [''],
      country: [''],
      category: [''],
      subcategory: [''],
      notes: ['']
    });
  }

  ngOnInit() {
    this.fromCompanyId = this.route.snapshot.queryParamMap.get('fromCompany');
    this.fromCompanyName = this.route.snapshot.queryParamMap.get('fromCompanyName');
    this.fromCompanyYear = this.route.snapshot.queryParamMap.get('year');

    // Subscribe to route parameter changes to handle navigation between different large shareholders
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const id = params.get('id');
        this.largeShareholderId = id;

        if (this.largeShareholderId) {
          const year = this.route.snapshot.queryParamMap.get('year');
          this.loadLargeShareholderData(year ? parseInt(year) : undefined);
          // Load connections, pyramid entries, and holdings
          this.loadConnections();
          this.loadPyramidEntriesByYear();
          this.loadHoldings();
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadLargeShareholderData(year?: number, skipYearPatch: boolean = false) {
    if (!this.largeShareholderId) return;

    this.spinnerService.show();
    this.largeShareholderService.getLargeShareholderById(
      this.largeShareholderId,
      year
    ).subscribe({
      next: (data) => {
        this.largeShareholderData = data;
        this.showYearWarning = false;

        // Prepare patch object
        const patchData: any = {
          fullName: data.name,
          address: data.address,
          website: data.website,
          email: data.email,
          country: data.countryId ? { id: data.countryId, name: data.countryName } : null,
          notes: data.notes
        };

        // Only patch year if not triggered by year change
        if (!skipYearPatch) {
          patchData.year = year?.toString();
        }

        // Patch basic fields first (without category and subcategory)
        this.form.patchValue(patchData, { emitEvent: false });

        // Patch category to trigger subcategories population
        if (data.categoryId) {
          this.form.patchValue({
            category: { id: data.categoryId, name: data.categoryName }
          }, { emitEvent: false });
        } else {
          this.form.patchValue({
            category: null
          }, { emitEvent: false });
        }

        // Wait for subcategories to be populated, then patch subcategory
        setTimeout(() => {
          if (data.subCategoryId) {
            this.form.patchValue({
              subcategory: { id: data.subCategoryId, name: data.subCategoryName }
            }, { emitEvent: false });
          } else {
            this.form.patchValue({
              subcategory: null
            }, { emitEvent: false });
          }
        }, 100);

        this.spinnerService.hide();
      },
      error: (error: any) => {
        this.spinnerService.hide();

        const apiError = error.error as ApiErrorResponse;

        if (apiError.errorCode === ErrorCodes.NFO_YEAR_NO_RECORD) {

          // Reset form fields except year
          const currentYear = this.form.get('year')?.value;
          this.form.reset();
          this.form.patchValue({ year: currentYear }, { emitEvent: false });
          this.showYearWarning = true;

        } else if (apiError.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
        } else {
          this.toast.error('Error loading large shareholder', {
          iconTheme: {
            primary: 'var(--mat-sys-error)',
            secondary: '#FFF',
          }});
        }

        console.error('Error loading large-shareholder:', error);

        // Clear connections on error
        this.connections = [];
      }
    });
  }

  loadConnections() {
    if (!this.largeShareholderId) return;

    this.largeShareholderService.getLargeShareholderConnections(this.largeShareholderId).subscribe({
      next: (connections) => {
        this.connections = connections;
      },
      error: (error) => {
        console.error('Error loading connections:', error);
        this.connections = [];
      }
    });
  }

  loadPyramidEntriesByYear() {
    if (!this.largeShareholderId) return;

    this.largeShareholderService.getPyramidEntriesByYear(this.largeShareholderId).subscribe({
      next: (data) => {
        // Store raw data for passing to pyramid component
        this.pyramidEntriesByYearRaw = data;

        // Convert the grouped data to a flat list using the helper method
        this.pyramidEntriesByYear = Helpers.convertPyramidEntriesByYearToList(data);

        // Convert to entity summaries with year-percentage data
        this.pyramidEntitySummaries = Helpers.convertPyramidEntriesByYearToEntitySummaries(data);
      },
      error: (error) => {
        console.error('Error loading pyramid entries by year:', error);
        this.pyramidEntriesByYear = [];
        this.pyramidEntriesByYearRaw = undefined;
        this.pyramidEntitySummaries = [];
      }
    });
  }

  loadHoldings() {
    if (!this.largeShareholderId) return;

    this.largeShareholderService.getHoldings(this.largeShareholderId).subscribe({
      next: (data) => {
        this.holdings = data;
      },
      error: (error) => {
        console.error('Error loading holdings:', error);
        this.holdings = null;
      }
    });
  }

  onYearChange(year: string) {
    // Update the URL query parameter
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { year: year },
      queryParamsHandling: 'merge'
    });

    // Load data for the new year, skip patching the year field
    this.loadLargeShareholderData(parseInt(year), true);
  }

  onSubmit() {
    if (this.form.valid && this.largeShareholderId) {
      this.spinnerService.show();

      const formValue = this.form.value;
      const request = {
        year: parseInt(formValue.year),
        name: formValue.fullName,
        address: formValue.address || '',
        website: formValue.website || '',
        email: formValue.email || '',
        countryId: formValue.country?.id || formValue.country,
        categoryId: formValue.category?.id || formValue.category,
        subCategoryId: formValue.subcategory?.id || formValue.subcategory || '',
        notes: formValue.notes || ''
      };

      this.largeShareholderService.updateLargeShareholder(this.largeShareholderData?.id!, request).subscribe({
        next: () => {
          this.spinnerService.hide();
          this.toast.success('Large shareholder updated successfully', {
            iconTheme: {
              primary: 'var(--mat-sys-primary)',
              secondary: '#FFF',
            }
          });
          // When editing from a company's ownership tab, return to it.
          // When editing from the list, stay on the page and silently
          // re-fetch so derived data stays in sync with the saved values.
          if (this.fromCompanyId) {
            this.navigateBack();
          } else {
            this.loadLargeShareholderData(request.year, true);
          }
        },
        error: (error: any) => {
          this.spinnerService.hide();
          this.toast.error('Error updating large shareholder', {
            iconTheme: {
              primary: 'var(--mat-sys-error)',
              secondary: '#FFF',
            }
          });
          console.error('Error updating large-shareholder:', error);
        }
      });
    }
  }

  onCancel() {
    this.navigateBack();
  }

  private navigateBack() {
    if (this.fromCompanyId) {
      this.router.navigate(['/companies/edit', this.fromCompanyId], {
        queryParams: { tab: 'ownership', year: this.fromCompanyYear }
      });
    } else {
      this.router.navigate(['/large-shareholders/list']);
    }
  }

  get isPyramidTabActive(): boolean {
    return this.largeShareholderForm?.isPyramidTabActive() ?? false;
  }

  get isConnectionTabActive(): boolean {
    return this.largeShareholderForm?.isConnectionTabActive() ?? false;
  }

  onAddPyramidItem() {
    if (!this.largeShareholderId) return;

    const dialogRef = this.dialog.open(AddPyramidDialogComponent, {
      width: '800px',
      data: {
        largeShareholderId: this.largeShareholderId,
        year: parseInt(this.form.get('year')?.value || '0')
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        // Reload pyramid data after successful addition
        const currentYear = parseInt(this.form.get('year')?.value || '0');
        this.loadLargeShareholderData(currentYear, true);
        this.loadPyramidEntriesByYear();
      }
    });
  }

  onAddConnectionItem() {
    if (!this.largeShareholderId) return;

    const dialogRef = this.dialog.open(AddConnectionDialogComponent, {
      width: '800px',
      data: {
        largeShareholderId: this.largeShareholderId,
        year: parseInt(this.form.get('year')?.value || '0')
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Reload connections - API call is handled in the dialog
        this.loadConnections();
      }
    });
  }

  onDeleteConnection(connectionId: string) {
    // Find the connection to get its name for the confirmation dialog
    const connection = this.connections.find(c => c.id === connectionId);
    const itemName = connection?.name || 'this connection';

    this.confirmationDialogService.openDeleteConfirmationDialog(itemName, `Are you sure you want to remove the connection to "${itemName}"? This action cannot be undone.`).subscribe((result: boolean) => {
      if (result) {
        this.spinnerService.show();
        this.largeShareholderService.deleteConnection(connectionId).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Connection deleted successfully', {
              iconTheme: {
                primary: 'var(--mat-sys-primary)',
                secondary: '#FFF',
              }
            });
            // Reload connections
            this.loadConnections();
          },
          error: (error) => {
            this.spinnerService.hide();
            this.toast.error('Error deleting connection', {
              iconTheme: {
                primary: 'var(--mat-sys-error)',
                secondary: '#FFF',
              }
            });
            console.error('Error deleting connection:', error);
          }
        });
      }
    });
  }

  onEditPyramidItem(pyramidId: string) {
    if (!this.largeShareholderId) return;

    // Find the pyramid item to edit
    const pyramidItem = this.largeShareholderData?.pyramid.find(p => p.id === pyramidId);
    if (!pyramidItem) {
      this.toast.error('Pyramid item not found');
      return;
    }

    const dialogRef = this.dialog.open(EditPyramidDialogComponent, {
      width: '800px',
      data: {
        pyramidItem: pyramidItem,
        largeShareholderId: this.largeShareholderId,
        pyramidEntriesByYear: this.pyramidEntriesByYearRaw
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        // Reload pyramid data after successful update
        const currentYear = parseInt(this.form.get('year')?.value || '0');
        this.loadLargeShareholderData(currentYear, true);
        this.loadPyramidEntriesByYear();
      }
    });
  }

  onDeletePyramidItem(pyramidId: string) {
    // Find the pyramid item to get its name for the confirmation dialog
    const pyramidItem = this.largeShareholderData?.pyramid.find(p => p.id === pyramidId);
    const itemName = pyramidItem ? this.getPyramidItemName(pyramidItem) : 'this pyramid item';
    const currentYear = parseInt(this.form.get('year')?.value || '0');

    // Find all years where this entity exists
    const allYears = this.findYearsForEntity(pyramidItem);

    // Open custom delete dialog
    const dialogRef = this.dialog.open(DeletePyramidDialogComponent, {
      width: '640px',
      data: {
        itemName,
        currentYear,
        allYears
      }
    });

    dialogRef.afterClosed().subscribe((result: DeletePyramidDialogResult | null) => {
      if (result && result.selectedYears && result.selectedYears.length > 0) {
        this.spinnerService.show();

        // Delete the selected years
        this.deleteSelectedYears(pyramidId, itemName, result.selectedYears);
      }
    });
  }

  private findYearsForEntity(pyramidItem: any): number[] {
    if (!pyramidItem || !this.pyramidEntriesByYearRaw) {
      return [];
    }

    // Helper function to check if two pyramid entries represent the same entity
    const isSameEntity = (p1: any, p2: any): boolean => {
      if (p1.directorId && p2.directorId && p1.directorId === p2.directorId) return true;
      if (p1.pyramidalShareholderId && p2.pyramidalShareholderId && p1.pyramidalShareholderId === p2.pyramidalShareholderId) return true;
      if (p1.pyramidLargeShareholderId && p2.pyramidLargeShareholderId && p1.pyramidLargeShareholderId === p2.pyramidLargeShareholderId) return true;
      if (p1.companyId && p2.companyId && p1.companyId === p2.companyId) return true;
      if (p1.opbCompanyId && p2.opbCompanyId && p1.opbCompanyId === p2.opbCompanyId) return true;
      return false;
    };

    // Find all years where this entity exists
    const years: number[] = [];
    Object.keys(this.pyramidEntriesByYearRaw).forEach(yearStr => {
      const year = parseInt(yearStr, 10);
      const yearPyramids = this.pyramidEntriesByYearRaw![yearStr];

      if (yearPyramids.some(p => isSameEntity(pyramidItem, p))) {
        years.push(year);
      }
    });

    // Sort years in descending order
    return years.sort((a, b) => b - a);
  }

  private deleteSelectedYears(pyramidId: string, itemName: string, selectedYears: number[]) {
    // Find the pyramid item to get its entity identity (stable across years)
    const pyramidItem = this.largeShareholderData?.pyramid.find(p => p.id === pyramidId);
    if (!pyramidItem || !this.largeShareholderId) {
      this.spinnerService.hide();
      this.toast.error('Pyramid item not found', {
        iconTheme: {
          primary: 'var(--mat-sys-error)',
          secondary: '#FFF',
        }
      });
      return;
    }

    if (!selectedYears || selectedYears.length === 0) {
      this.spinnerService.hide();
      return;
    }

    // Single atomic request: the server re-resolves the correct historical row for each year by the
    // node's entity identity, so a multi-year span (migrated/merged data) is deleted correctly.
    // Sending the entity ids — not a row id — is what makes this robust: a client-precomputed row id
    // goes stale the instant the first year's period split reassigns it.
    const request: DeletePyramidYearsRequest = {
      largeShareholderId: this.largeShareholderId,
      pyramidLargeShareholderId: pyramidItem.pyramidLargeShareholderId || undefined,
      pyramidalShareholderId: pyramidItem.pyramidalShareholderId || undefined,
      companyId: pyramidItem.companyId || undefined,
      opbCompanyId: pyramidItem.opbCompanyId || undefined,
      directorId: pyramidItem.directorId || undefined,
      years: selectedYears
    };

    this.largeShareholderService.deletePyramidYears(request).subscribe({
      next: () => {
        this.spinnerService.hide();
        this.toast.success(
          `Pyramid item "${itemName}" deleted for ${selectedYears.length} ${selectedYears.length === 1 ? 'year' : 'years'}`,
          {
            iconTheme: {
              primary: 'var(--mat-sys-primary)',
              secondary: '#FFF',
            }
          }
        );

        // Reload pyramid data (use first selected year or current year from form)
        const reloadYear = selectedYears.length > 0 ? selectedYears[0] : parseInt(this.form.get('year')?.value || '0');
        this.loadLargeShareholderData(reloadYear, true);
        this.loadPyramidEntriesByYear();
      },
      error: (error) => {
        this.spinnerService.hide();
        this.toast.error('Error deleting pyramid items', {
          iconTheme: {
            primary: 'var(--mat-sys-error)',
            secondary: '#FFF',
          }
        });
        console.error('Error deleting pyramid items:', error);
      }
    });
  }

  onDeleteEntityYear(event: DeleteEntityYearEvent) {
    // Find the pyramid entry for this entity and year
    if (!this.pyramidEntriesByYearRaw) {
      this.toast.error('Unable to find pyramid data', {
        iconTheme: {
          primary: 'var(--mat-sys-error)',
          secondary: '#FFF',
        }
      });
      return;
    }

    const yearPyramids = this.pyramidEntriesByYearRaw[event.year.toString()];
    if (!yearPyramids) {
      this.toast.error(`No pyramid data found for year ${event.year}`, {
        iconTheme: {
          primary: 'var(--mat-sys-error)',
          secondary: '#FFF',
        }
      });
      return;
    }

    // Find the pyramid entry that matches this entity
    let pyramidEntry;
    switch (event.entityType) {
      case 'Director':
        pyramidEntry = yearPyramids.find(p => p.directorId === event.entityId);
        break;
      case 'Pyramidal Shareholder':
        pyramidEntry = yearPyramids.find(p => p.pyramidalShareholderId === event.entityId);
        break;
      case 'Pyramid Large Shareholder':
        pyramidEntry = yearPyramids.find(p => p.pyramidLargeShareholderId === event.entityId);
        break;
      case 'Company':
        pyramidEntry = yearPyramids.find(p => p.companyId === event.entityId);
        break;
      case 'Opb Company':
        pyramidEntry = yearPyramids.find(p => p.opbCompanyId === event.entityId);
        break;
    }

    if (!pyramidEntry) {
      this.toast.error('Pyramid entry not found', {
        iconTheme: {
          primary: 'var(--mat-sys-error)',
          secondary: '#FFF',
        }
      });
      return;
    }

    const itemName = this.getPyramidItemName(pyramidEntry);

    // Show simple confirmation dialog
    this.confirmationDialogService.openDeleteConfirmationDialog(
      itemName,
      `Are you sure you want to delete "${itemName}" for year ${event.year}? This action cannot be undone.`
    ).subscribe((result: boolean) => {
      if (result) {
        this.spinnerService.show();
        this.largeShareholderService.deletePyramidEntry({
          id: pyramidEntry.id,
          year: event.year
        }).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success(`"${itemName}" deleted for year ${event.year}`, {
              iconTheme: {
                primary: 'var(--mat-sys-primary)',
                secondary: '#FFF',
              }
            });
            // Reload pyramid data
            const currentYear = parseInt(this.form.get('year')?.value || '0');
            this.loadLargeShareholderData(currentYear, true);
            this.loadPyramidEntriesByYear();
          },
          error: (error) => {
            this.spinnerService.hide();
            this.toast.error('Error deleting pyramid item', {
              iconTheme: {
                primary: 'var(--mat-sys-error)',
                secondary: '#FFF',
              }
            });
            console.error('Error deleting pyramid item:', error);
          }
        });
      }
    });
  }

  onDeletePyramidYear(event: DeletePyramidYearEvent) {
    // Find the pyramid item to get its name
    if (!this.pyramidEntriesByYearRaw) {
      this.toast.error('Unable to find pyramid data', {
        iconTheme: {
          primary: 'var(--mat-sys-error)',
          secondary: '#FFF',
        }
      });
      return;
    }

    const yearPyramids = this.pyramidEntriesByYearRaw[event.year.toString()];
    if (!yearPyramids) {
      this.toast.error(`No pyramid data found for year ${event.year}`, {
        iconTheme: {
          primary: 'var(--mat-sys-error)',
          secondary: '#FFF',
        }
      });
      return;
    }

    const pyramidEntry = yearPyramids.find(p => p.id === event.pyramidId);
    if (!pyramidEntry) {
      this.toast.error('Pyramid entry not found', {
        iconTheme: {
          primary: 'var(--mat-sys-error)',
          secondary: '#FFF',
        }
      });
      return;
    }

    const itemName = this.getPyramidItemName(pyramidEntry);

    // Show simple confirmation dialog
    this.confirmationDialogService.openDeleteConfirmationDialog(
      itemName,
      `Are you sure you want to delete "${itemName}" for year ${event.year}? This action cannot be undone.`
    ).subscribe((result: boolean) => {
      if (result) {
        this.spinnerService.show();
        this.largeShareholderService.deletePyramidEntry({
          id: event.pyramidId,
          year: event.year
        }).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success(`"${itemName}" deleted for year ${event.year}`, {
              iconTheme: {
                primary: 'var(--mat-sys-primary)',
                secondary: '#FFF',
              }
            });
            // Reload pyramid data
            const currentYear = parseInt(this.form.get('year')?.value || '0');
            this.loadLargeShareholderData(currentYear, true);
            this.loadPyramidEntriesByYear();
          },
          error: (error) => {
            this.spinnerService.hide();
            this.toast.error('Error deleting pyramid item', {
              iconTheme: {
                primary: 'var(--mat-sys-error)',
                secondary: '#FFF',
              }
            });
            console.error('Error deleting pyramid item:', error);
          }
        });
      }
    });
  }

  private getPyramidItemName(pyramid: any): string {
    return pyramid.pyramidLargeShareholderName
      || pyramid.pyramidalShareholderName
      || pyramid.companyName
      || pyramid.opbCompanyName
      || pyramid.directorName
      || 'Unknown';
  }
}
